import { useCallback } from 'react';
import {
  type AnyAction,
  type AsyncThunkAction,
  type Dispatch,
  unwrapResult,
} from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import DispatchWithUnwrapError from 'errors/DispatchWithUnwrapError';

interface UnwrappableAction {
  payload: unknown;
  meta?: unknown;
  error?: unknown;
}

interface ErrorObject {
  message: string;
  name: string;
  responseStatus?: string;
  code?: string;
  stack?: string;
  requestDetails?: unknown;
}

interface AsyncThunkConfig {
  state?: unknown;
  dispatch?: Dispatch;
  extra?: unknown;
  rejectValue?: unknown;
  serializedErrorType?: unknown;
}

const isObjectError = (value: unknown): value is ErrorObject => {
  return value instanceof Object && !!value && value.hasOwnProperty('message');
};

export type DispatchWithUnwrap = <Returned, ThunkArg, ThunkApiConfig extends AsyncThunkConfig>(
  action: AsyncThunkAction<Returned, ThunkArg, ThunkApiConfig>,
) => Promise<Returned>;

const useDispatchWithUnwrap = (): DispatchWithUnwrap => {
  const dispatch = useDispatch();

  return useCallback(
    async <Returned, ThunkArg, ThunkApiConfig extends AsyncThunkConfig>(
      action: AsyncThunkAction<Returned, ThunkArg, ThunkApiConfig>,
    ) => {
      try {
        const result = await dispatch(action as unknown as AnyAction);

        return unwrapResult(result as unknown as UnwrappableAction) as Returned;
      } catch (error) {
        if (error instanceof Error) {
          throw error;
        }

        if (isObjectError(error)) {
          throw new DispatchWithUnwrapError(
            error.message,
            error.name,
            error,
            error.responseStatus,
            error.code,
            error.stack,
            error.requestDetails,
          );
        }

        throw error;
      }
    },
    [dispatch],
  );
};

export default useDispatchWithUnwrap;
